import React, { useCallback, useEffect, useState } from 'react';
import { VideoFile } from '../pages/mofamily/types';
import {
  VideoBox, listBoxes, createBox, getBox, patchBox, deleteBox, tieGroup,
  addItem, removeItem, updateItem, bindSlot, findEmptySlotNumber,
  addChildBoxItem, EntryView,
} from '../utils/videobox';
import { moFamilyStore } from '../stores/moFamilyStore';
import { isExpired, eventDDay , toLocalInput, fromLocalInput } from '../utils/datetime';

/**
 * 영상 박스 패널 — "내 영상 목록" 옆에 놓는다. 박스 = 시간대별 재생 스케줄 묶음.
 * 박스 카드는 드래그해서 슬롯에 넣을 수 있고(application/x-videobox-drag),
 * 스케줄러가 시각에 맞는 영상을 슬롯에 기록한다. 카드에는 썸네일이 없다(박스는 여러 영상).
 * 편집은 모달: 이름, 기본 영상(default), 시간대 항목(시작/종료, 분 단위).
 */


const titleOf = (videos: VideoFile[], fileHash: string | null): string =>
  fileHash ? (videos.find((v) => v.file_hash === fileHash)?.title ?? fileHash.slice(0, 8)) : '(없음)';

/** 초 → m:ss (없으면 null). */
const pad = (n: number) => String(n).padStart(2, '0');
const fmtDur = (s?: number): string | null => (s ? `${Math.floor(s / 60)}:${pad(Math.floor(s % 60))}` : null);

/**
 * 편집 모달 안의 드래그 소스 카드 — 하이어라키 "내 영상 목록"의 VideoThumb 스타일을 재현.
 * application/x-video-drag = {fileHash, title} 로 드래그(모달 드롭존이 받는 형식과 동일).
 */
const BoxVideoThumb: React.FC<{ video: VideoFile; onPick?: () => void }> = ({ video, onPick }) => {
  const expired = isExpired(video.expires_at);
  const dday = expired ? null : eventDDay(video.event_start_at);
  return (
  <div
    draggable
    onDragStart={(e) => {
      e.dataTransfer.setData('application/x-video-drag', JSON.stringify({ fileHash: video.file_hash, title: video.title }));
      e.dataTransfer.effectAllowed = 'copy';
      (e.currentTarget as HTMLDivElement).style.opacity = '0.3';
    }}
    onDragEnd={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
    onClick={onPick}
    className="cursor-pointer hover:opacity-80"
    title={expired ? `${video.title} (만료됨 — 재생 안 됨)` : `${video.title} — 클릭 또는 드래그해서 기간 항목 추가`}
  >
    <div className={`w-16 h-9 border border-gray-300 overflow-hidden relative ${expired ? 'opacity-40' : ''}`}>
      {expired ? (
        <span className="absolute top-0 right-0 z-10 px-1 text-[8px] leading-tight bg-gray-700 text-white rounded-bl">만료</span>
      ) : dday != null ? (
        <span className="absolute top-0 right-0 z-10 px-1 text-[8px] leading-tight bg-blue-600 text-white rounded-bl">D-{dday}</span>
      ) : null}
      {video.thumbnail_path ? (
        <img src={`/mofile/thumbnail/${video.thumbnail_path}`} alt={video.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 text-[10px]">▶</div>
      )}
    </div>
    <p className="text-[10px] truncate w-16 mt-0.5">{video.title}</p>
    {fmtDur(video.duration) && <p className="text-[10px] text-gray-500">{fmtDur(video.duration)}</p>}
  </div>
  );
};

interface Props {
  moFamilyHash: string;
  myVideos: VideoFile[];
  /** 박스↔슬롯 바인딩이 바뀌었을 때(하이어라키 오버레이 리프레시용). */
  onChanged?: () => void;
}

export const VideoBoxPanel: React.FC<Props> = ({ moFamilyHash, myVideos, onChanged }) => {
  const [boxes, setBoxes] = useState<VideoBox[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 박스 생성 다이얼로그 — 이름 + 타입(라디오, 배타 선택).
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'schedule' | 'sequence' | 'group'>('schedule');

  const refresh = useCallback(async () => {
    try {
      setBoxes(await listBoxes());  // 내 박스 전부(소유=peer, 107) — 보는 family 무관
    } catch (e) {
      setError(e instanceof Error ? e.message : '박스 목록을 불러오지 못했습니다.');
    }
  }, [moFamilyHash]);

  useEffect(() => { void refresh(); }, [refresh]);

  const openCreate = () => { setNewName(''); setNewType('schedule'); setCreating(true); };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const box = await createBox(newName.trim(), newType);
      setCreating(false);
      await refresh();
      setEditingId(box.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : '박스 생성 실패');
    }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm md:text-lg font-medium flex items-center gap-1">
          영상 박스
          <span className="relative inline-block group">
            <span className="text-blue-500 cursor-help text-sm">?</span>
            <div className="hidden group-hover:block absolute bottom-full left-0 w-72 p-3 mb-2 text-xs bg-gray-800 text-white z-10 font-normal text-left leading-relaxed">
              영상 박스에 여러 일정의 영상을 담고, 영상 박스를 슬롯에 넣으면, 영상 박스에서 현재와 가장 가까운 일정의 영상이 자동으로 재생됩니다.
              <div className="absolute -bottom-2 left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
            </div>
          </span>
        </h3>
      </div>

      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

      {boxes.length === 0 && (
        <p className="text-xs text-gray-400 leading-relaxed mb-2">
          ※ 예를 들어 “이번 주 토요일” 행사와 “다음 주 토요일” 행사를 토요일 박스에 담으면, “이번 주 토요일” 행사가 끝나면 자동으로 “다음 주 토요일” 영상으로 바뀝니다.
        </p>
      )}
      {/* 모바일에선 gap 을 줄여(gap-1) w-28 카드 3개가 한 줄에 들어가게 하고,
          데스크톱은 원래 간격(gap-3) 유지. */}
      <div className="flex flex-wrap gap-1 md:gap-3">
        {boxes.map((box) => (
          <div
            key={box.id}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('application/x-videobox-drag', JSON.stringify({ boxId: box.id, name: box.name }));
              e.dataTransfer.effectAllowed = 'copy';
              (e.currentTarget as HTMLDivElement).style.opacity = '0.3';
            }}
            onDragEnd={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
            onClick={() => setEditingId(box.id)}
            className="cursor-pointer border-2 border-dashed border-purple-400 bg-purple-50 hover:border-purple-600 px-3 py-2 w-[110px] md:w-28"
            title="클릭: 편집 · 드래그: 슬롯에 넣기"
          >
            <div className="text-xs font-semibold text-purple-700 truncate">{box.boxType === 'sequence' ? '🔁' : box.boxType === 'group' ? '🗂️' : '📦'} {box.name}</div>
            <div className="text-[10px] text-gray-500 mt-1">{box.boxType === 'sequence' ? '시퀀싱 박스' : box.boxType === 'group' ? '그룹 박스' : '스케줄 박스'}</div>
          </div>
        ))}
        {/* + 박스 추가 — 목록 안의 항목 형태 */}
        <button
          type="button"
          onClick={openCreate}
          title="새 영상 박스 추가"
          className="cursor-pointer border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 px-3 py-2 w-[110px] md:w-28 flex flex-col items-center justify-center text-gray-400 hover:text-blue-500"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
          <div className="text-[10px] mt-1">박스 추가</div>
        </button>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setCreating(false)}>
          <div className="bg-white w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-3">새 영상 박스</h3>
            <label className="block text-sm font-medium mb-1">이름</label>
            <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) void handleCreate(); }}
              placeholder="예: 토요일 행사"
              className="border border-gray-300 px-2 py-1 w-full mb-4" />

            <div className="block text-sm font-medium mb-2">박스 종류 <span className="text-xs text-gray-400">(하나 선택)</span></div>
            <div className="space-y-2 mb-5">
              {([
                { v: 'schedule', icon: '📦', title: '스케줄 박스',
                  desc: '일정 기반 — 지금 시각에 해당하는 항목 하나를 사용합니다. (여러 항목이 겹치면 나중에 시작한 항목이 우선)' },
                { v: 'sequence', icon: '🔁', title: '시퀀싱 박스',
                  desc: '순서 순환 — 재생이 끝날 때마다 다음 항목으로 넘어갑니다. (예: 월~일 박스를 넣어 요일마다 순환)' },
                { v: 'group', icon: '🗂️', title: '그룹 박스',
                  desc: '단순 묶음 — 이 박스를 넣는 것은 안의 영상들을 낱개로 전부 넣는 것과 같습니다. (같은 시기 영상 여러 개를 한 번에 다루기)' },
              ] as const).map((o) => (
                <label key={o.v}
                  className={`flex items-start gap-2 border p-3 cursor-pointer ${newType === o.v ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'}`}>
                  <input type="radio" name="box-type" value={o.v} checked={newType === o.v}
                    onChange={() => setNewType(o.v)} className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{o.icon} {o.title}</span>
                    <span className="block text-xs text-gray-500 leading-relaxed">{o.desc}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-sm">취소</button>
              <button disabled={!newName.trim()} onClick={handleCreate}
                className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white text-sm disabled:opacity-40">만들기</button>
            </div>
          </div>
        </div>
      )}

      {editingId !== null && (
        <BoxEditModal
          boxId={editingId}
          moFamilyHash={moFamilyHash}
          myVideos={myVideos}
          onClose={() => { setEditingId(null); void refresh(); onChanged?.(); }}
          onDeleted={() => { setEditingId(null); void refresh(); onChanged?.(); }}
        />
      )}
    </div>
  );
};

// ── 편집 모달 ─────────────────────────────────────────────────────────
const BoxEditModal: React.FC<{
  boxId: number;
  moFamilyHash: string;
  myVideos: VideoFile[];
  onClose: () => void;
  onDeleted: () => void;
}> = ({ boxId, moFamilyHash, myVideos, onClose, onDeleted }) => {
  const [name, setName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [defaultFileHash, setDefaultFileHash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 083 중첩/시퀀싱: 박스 타입·통합 항목(entries, 하위박스 포함)·하위박스 선택기
  const [boxType, setBoxType] = useState<'schedule' | 'sequence' | 'group'>('schedule');
  const [entries, setEntries] = useState<EntryView[]>([]);
  // 항목이 박스인 경우 그 안을 **펼쳐서** 보여준다(접힘 없음) — 그룹의 투명성("낱개로
  // 넣은 것과 같다")을 화면이 그대로 보여줘야 "왜 흩어져/같이 나오지?"가 설명된다.
  const [childEntries, setChildEntries] = useState<Record<number, EntryView[]>>({});
  const [siblingBoxes, setSiblingBoxes] = useState<VideoBox[]>([]);

  // 박스를 빈 슬롯에 넣기(모바일 드래그 대체 — 단일 영상 모달과 동일 UX).
  const [binding, setBinding] = useState(false);
  const [bindMsg, setBindMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const bindToEmptySlot = async () => {
    setBinding(true); setBindMsg(null);
    try {
      const setName = moFamilyStore.getState().editingSlotSet || 'A';
      const res = await fetch(`/mofamily/${moFamilyHash}/slot-sets`, { credentials: 'include' });
      if (!res.ok) throw new Error('슬롯 정보를 불러오지 못했습니다.');
      const data = await res.json();
      const sets: any[] = data.data?.sets || [];
      const set = sets.find((s: any) => s.setName === setName) || sets.find((s: any) => s.isActive) || sets[0];
      const slots: any[] = set?.slots || [];
      if (slots.length === 0) { setBindMsg({ ok: false, text: '슬롯이 없습니다. 슬롯 관리에서 먼저 슬롯을 추가하세요.' }); return; }
      // 빈 슬롯 판정은 findEmptySlotNumber 한 곳으로 모았다(영상 넣기 경로와 공유).
      const emptySlotNumber = await findEmptySlotNumber(moFamilyHash, set.setName, slots);
      if (emptySlotNumber == null) { setBindMsg({ ok: false, text: `세트 ${set.setName}에 빈 슬롯이 없습니다.` }); return; }
      await bindSlot(boxId, moFamilyHash, emptySlotNumber, set.setName);
      const refreshed = await fetch(`/mofamily/${moFamilyHash}/slot-sets`, { credentials: 'include' });
      if (refreshed.ok) { const rd = await refreshed.json(); moFamilyStore.getState().setSlotSets(moFamilyHash, rd.data.sets); }
      // store 갱신으로 슬롯 표시가 이미 반영됨 — onSaved(부모 refresh)는 모달을 닫으므로 생략.
      setBindMsg({ ok: true, text: `세트 ${set.setName}의 슬롯 ${emptySlotNumber}에 넣었습니다.` });
    } catch (e: any) {
      setBindMsg({ ok: false, text: e?.message || '슬롯에 넣지 못했습니다.' });
    } finally { setBinding(false); }
  };

  // 드롭된 영상 → 시작/종료 기간을 묻는 인라인 폼
  // 넣기 대기 — 영상(kind:'video')과 하위 박스(kind:'box')를 같은 흐름으로 다룬다.
  const [pendingDrop, setPendingDrop] = useState<
    { kind: 'video'; fileHash: string; title: string } | { kind: 'box'; childBoxId: number; title: string } | null
  >(null);
  const [dropStart, setDropStart] = useState('');
  const [dropEnd, setDropEnd] = useState('');
  const [dragOver, setDragOver] = useState(false);
  // 항목 기간 인라인 편집.
  const [editItemId, setEditItemId] = useState<number | null>(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await getBox(boxId);
      setName(d.box?.name ?? '');
      setBoxType(d.box?.boxType ?? 'schedule');
      setDefaultFileHash(d.defaultFileHash);
      setEntries(d.entries);
      // 자식 박스 1단계 펼침 — 손자 박스는 이름 행으로만(더 깊은 건 그 박스를 열어서).
      const childIds = [...new Set(d.entries.filter((en) => en.childBoxId !== null).map((en) => en.childBoxId as number))];
      const pairs = await Promise.all(childIds.map(async (cid) => {
        try { return [cid, (await getBox(cid)).entries] as const; }
        catch { return [cid, []] as const; }
      }));
      setChildEntries(Object.fromEntries(pairs));
      // 하위 박스 후보(같은 family, 자기 제외). 사이클은 서버가 최종 거부.
      setSiblingBoxes((await listBoxes()).filter((b) => b.id !== boxId));  // 중첩 후보 = 내 박스 전부
    } catch (e) {
      setError(e instanceof Error ? e.message : '박스를 불러오지 못했습니다.');
    }
  }, [boxId]);

  useEffect(() => { void load(); }, [load]);

  const saveMeta = async (patch: { name?: string; defaultFileHash?: string | null }) => {
    setBusy(true); setError(null);
    try { await patchBox(boxId, patch); /* onSaved 생략 — 부모 갱신은 모달 닫을 때(리마운트 방지) */ }
    catch (e) { setError(e instanceof Error ? e.message : '저장 실패'); }
    finally { setBusy(false); }
  };

  /** 영상 드롭 → 기간 폼 오픈(시작=현재 기본). */
  /** 영상 선택(클릭·드롭 공통) → 재생 기간 폼 오픈. 시작 기본값 = 행사 시작 -(1주-1시간), 없으면 현재. */
  const pickVideo = (fileHash: string, title: string) => {
    const now = Date.now();
    // 직전 영상은 자기 행사 시작 1시간 뒤까지 노출되고, 이 영상이 그 시점부터 한 주간 재생된다.
    const dropped = myVideos.find((v) => v.file_hash === fileHash);
    const WEEK_MINUS_HOUR_MS = (7 * 24 - 1) * 60 * 60 * 1000;
    const startMs = dropped?.event_start_at
      ? new Date(dropped.event_start_at).getTime() - WEEK_MINUS_HOUR_MS
      : now;
    setPendingDrop({ kind: 'video', fileHash, title });
    setDropStart(toLocalInput(startMs));
    setDropEnd(''); // 종료 기본값은 비움(무한) — 영상 자체 만료(expires_at)가 실질 상한
    setError(null);
  };

  /** 하위 박스 선택(클릭·드롭 공통) — 영상과 동일하게 기간 폼을 연다. */
  const pickChildBox = (childBoxId: number, title: string) => {
    setPendingDrop({ kind: 'box', childBoxId, title });
    setDropStart(toLocalInput(Date.now()));
    setDropEnd('');
    setError(null);
  };

  const onVideoDrop = (raw: string) => {
    try {
      const { fileHash, title } = JSON.parse(raw) as { fileHash?: string; title?: string };
      if (!fileHash) return;
      pickVideo(fileHash, title ?? fileHash.slice(0, 8));
    } catch { /* ignore */ }
  };

  /** 박스 카드 드래그(application/x-videobox-drag) → 하위 박스로 넣기. */
  const onBoxDrop = (raw: string) => {
    try {
      const { boxId: dropped, name } = JSON.parse(raw) as { boxId?: number; name?: string };
      if (!dropped || dropped === boxId) return;
      pickChildBox(dropped, name ?? `박스 #${dropped}`);
    } catch { /* ignore */ }
  };

  /** 기간 폼에서 확정 → 항목 추가. */
  const handleAddPending = async () => {
    if (!pendingDrop) return;
    if (!dropStart) { setError('시작 시각을 입력하세요.'); return; }
    const startMs = fromLocalInput(dropStart);
    const endMs = dropEnd ? fromLocalInput(dropEnd) : null;
    if (endMs !== null && !(startMs < endMs)) { setError('시작 < 종료 여야 합니다.'); return; }
    // ── 동률(같은 창) 감지 — 스케줄 박스는 승자 1개만 재생하므로, 같은 창을 낱개로
    //    넣으면 한쪽이 조용히 재생 0 이 된다(동률 그림자). 그림자를 원하는 실무는 없다
    //    (사용자 확정 2026-08-23) — **묻지 않고 그룹으로 묶고**, 결과를 피드백한다.
    if (boxType === 'schedule' && pendingDrop.kind === 'video') {
      const sameWindow = entries.filter((en) => {
        const enStart = new Date(en.startAt).getTime();
        const enEnd = en.endAt ? new Date(en.endAt).getTime() : null;
        return enStart === startMs && enEnd === endMs;
      });
      if (sameWindow.length > 0) {
        setBusy(true); setError(null);
        try {
          const r = await tieGroup(boxId, pendingDrop.fileHash, startMs, endMs);
          // 어디에 들어갔는지 반드시 보이게 — 상단 상태줄은 항목 영역에서 시야 밖이라
          // 놓친다(실사용 피드백 2026-08-23). alert 확인이 곧 마무리다.
          alert(r.created
            ? `같은 일정 항목이 있어 '${r.groupName}' 그룹을 새로 만들고,\n기존 항목과 이 영상을 그룹에 넣어 함께 재생되게 했습니다.`
            : `같은 일정의 그룹 '${r.groupName}' 에 넣었습니다.\n함께 재생됩니다.`);
          setPendingDrop(null); setDropEnd('');
          await load();
        } catch (e) {
          setError(e instanceof Error ? e.message : '그룹 묶기 실패');
        } finally { setBusy(false); }
        return;
      }
    }
    setBusy(true); setError(null);
    try {
      if (pendingDrop.kind === 'video') {
        await addItem(boxId, pendingDrop.fileHash, startMs, endMs);
      } else {
        await addChildBoxItem(boxId, pendingDrop.childBoxId, startMs, endMs);
      }
      setPendingDrop(null); setDropEnd('');
      // 모달 안 목록만 갱신 — onSaved(부모 refresh+onChanged)는 VideoBoxPanel 리마운트로
      // editingId 를 리셋해 모달을 닫아버린다. 항목 추가는 박스 이름·부모 목록을 안 바꾸므로 불필요.
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '항목 추가 실패');
    } finally { setBusy(false); }
  };

  const handleRemoveItem = async (itemId: number) => {
    setBusy(true); setError(null);
    try { await removeItem(itemId); await load(); /* onSaved 생략 — 모달 유지(위 handleAddPending 주석) */ }
    catch (e) { setError(e instanceof Error ? e.message : '항목 삭제 실패'); }
    finally { setBusy(false); }
  };

  const beginEditItem = (it: { id: number; startAt: number; endAt: number | null }) => {
    setEditItemId(it.id);
    setEditStart(toLocalInput(it.startAt));
    setEditEnd(it.endAt === null ? '' : toLocalInput(it.endAt));
    setError(null);
  };

  const handleUpdateItem = async () => {
    if (editItemId === null) return;
    if (!editStart) { setError('시작 시각을 입력하세요.'); return; }
    const startMs = fromLocalInput(editStart);
    const endMs = editEnd ? fromLocalInput(editEnd) : null;
    if (endMs !== null && !(startMs < endMs)) { setError('시작 < 종료 여야 합니다.'); return; }
    setBusy(true); setError(null);
    try {
      await updateItem(editItemId, startMs, endMs);
      setEditItemId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '항목 수정 실패');
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!confirm('이 영상 박스를 삭제하시겠습니까? 넣어둔 슬롯도 비워집니다.')) return;
    setBusy(true); setError(null);
    try { await deleteBox(boxId); onDeleted(); }
    catch (e) { setError(e instanceof Error ? e.message : '삭제 실패'); setBusy(false); }
  };

  const fmt = (ms: number) => new Date(ms).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });

  /**
   * 박스 항목 기간은 곧 **홍보 구간**이다. 그 영상의 **행사 시작·종료**를 옆에 띄워
   * "언제까지 홍보할지"를 암산하지 않게 한다 — 행사 정보는 영상에 이미 붙어 있는데
   * 이 화면에서만 안 보여서, 다른 탭을 열어 확인하고 돌아오는 왕복이 있었다.
   * 표시 전용이다(값을 바꾸지 않는다). 버튼을 누를 때만 기간에 반영한다.
   */
  const eventRefOf = (fileHash: string | null | undefined) => {
    if (!fileHash) return null;
    const v = myVideos.find((mv) => mv.file_hash === fileHash);
    if (!v?.event_start_at) return null;
    const startMs = new Date(v.event_start_at).getTime();
    if (Number.isNaN(startMs)) return null;
    const endRaw = v.expires_at ? new Date(v.expires_at).getTime() : NaN;
    const endMs = Number.isNaN(endRaw) ? null : endRaw;
    return { startMs, endMs };
  };

  /** 행사 참고 줄 + "행사 기간으로" 버튼(기간 칸을 그 값으로 채운다). */
  const EventRef: React.FC<{
    fileHash: string | null | undefined;
    onApply: (startMs: number, endMs: number | null) => void;
  }> = ({ fileHash, onApply }) => {
    const ref = eventRefOf(fileHash);
    if (!ref) return null;
    return (
      <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
        <span>
          행사 {fmt(ref.startMs)}
          {ref.endMs !== null && <> ~ {fmt(ref.endMs)}</>}
        </span>
        <button
          type="button"
          onClick={() => onApply(ref.startMs, ref.endMs)}
          className="px-1.5 py-0.5 border border-gray-300 rounded text-[10px] text-gray-600 hover:bg-gray-100"
          title="이 항목의 기간을 행사 시작·종료로 맞춥니다"
        >
          행사 기간으로
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            {boxType === 'sequence' ? '🔁' : boxType === 'group' ? '🗂️' : '📦'} 영상 박스 편집
            <select
              value={boxType}
              onChange={(e) => {
                const v = e.target.value as 'schedule' | 'sequence' | 'group';
                setBoxType(v);
                void patchBox(boxId, { boxType: v }).catch(() => setError('타입 변경 실패'));
              }}
              title="박스 타입 — 바꾸는 즉시 재생 방식이 바뀝니다"
              className={`text-xs font-medium px-1.5 py-0.5 rounded border-0 cursor-pointer ${
                boxType === 'sequence' ? 'bg-blue-100 text-blue-700'
                : boxType === 'group' ? 'bg-amber-100 text-amber-700'
                : 'bg-purple-100 text-purple-700'}`}
            >
              <option value="schedule">📦 스케줄</option>
              <option value="sequence">🔁 시퀀싱</option>
              <option value="group">🗂️ 그룹</option>
            </select>
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
        </div>

        {/* 빈 슬롯에 넣기 — 단일 영상 모달과 동일 UX(모바일 드래그 대체). 박스를 활성/편집 슬롯셋의 빈 슬롯에 바인딩. */}
        <div className="mb-4">
          <button
            type="button"
            disabled={binding}
            onClick={bindToEmptySlot}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
            {binding ? '넣는 중...' : '빈 슬롯에 넣기'}
          </button>
          {bindMsg && (
            <p className={`mt-1 text-sm ${bindMsg.ok ? 'text-green-600' : 'text-red-600'}`}>{bindMsg.text}</p>
          )}
        </div>

        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

        {/* 이름 — 연필로 편집(행사 시작 입력과 동일 방식) */}
        <label className="block text-sm font-medium mb-1">이름</label>
        {isEditingName ? (
          <div className="flex gap-2 mb-4">
            <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { void saveMeta({ name: editName }); setName(editName); setIsEditingName(false); } if (e.key === 'Escape') setIsEditingName(false); }}
              className="flex-1 border border-gray-300 px-2 py-1" />
            <button disabled={busy} onClick={async () => { await saveMeta({ name: editName }); setName(editName); setIsEditingName(false); }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 disabled:opacity-50">저장</button>
            <button disabled={busy} onClick={() => setIsEditingName(false)}
              className="text-gray-500 hover:text-gray-700 px-2 py-1">취소</button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-base font-medium">{name || '(이름 없음)'}</span>
            <button onClick={() => { setEditName(name); setIsEditingName(true); }} className="text-gray-400 hover:text-blue-500" title="이름 수정">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
        )}

        {/* 기본 영상 */}
        <label className="block text-sm font-medium mb-1">
          기본 영상 <span className="text-xs text-gray-400">(어느 시간대에도 해당 항목이 없을 때 재생)</span>
        </label>
        <select value={defaultFileHash ?? ''} disabled={busy}
          onChange={(e) => { const v = e.target.value || null; setDefaultFileHash(v); void saveMeta({ defaultFileHash: v }); }}
          className="w-full border border-gray-300 px-2 py-1 mb-4">
          <option value="">(없음 — 빈 슬롯)</option>
          {myVideos.map((v) => <option key={v.file_hash} value={v.file_hash}>{v.title}</option>)}
        </select>

        {/* 항목 목록 — 영상과 하위 박스를 구별 없이 한 목록에서 관리 */}
        <h3 className="text-sm font-medium mb-2">
          {boxType === 'sequence' ? '🔁 순환 항목' : boxType === 'group' ? '🗂️ 묶음 항목' : '시간대 항목'}
          <span className="text-xs text-gray-400 ml-1">
            {boxType === 'sequence'
              ? '(위에서부터 순서대로 — 재생이 끝날 때마다 다음 항목)'
              : boxType === 'group'
              ? '(전부 각자 재생됩니다 — 낱개로 넣은 것과 같음)'
              : '(겹치면 나중에 시작한 항목이 우선)'}
          </span>
        </h3>
        {entries.length === 0 ? (
          <div className="text-sm text-gray-500 mb-3">항목이 없습니다. 아래에서 영상이나 박스를 넣으세요.</div>
        ) : (
          <table className="w-full text-sm mb-3">
            <thead><tr className="text-left text-gray-500 border-b">
              {boxType === 'sequence' && <th className="py-1 w-6">#</th>}
              <th className="py-1">항목</th><th>시작</th><th>종료</th><th></th>
            </tr></thead>
            <tbody>
              {entries.map((e, idx) => {
                const startMs = new Date(e.startAt).getTime();
                const endMs = e.endAt ? new Date(e.endAt).getTime() : null;
                const video = e.fileHash ? myVideos.find((mv) => mv.file_hash === e.fileHash) : undefined;
                const ended = (endMs !== null && endMs <= Date.now()) || (e.fileHash ? isExpired(video?.expires_at) : false);
                const label = e.fileHash
                  ? `📹 ${titleOf(myVideos, e.fileHash)}`
                  : `${e.childBoxType === 'sequence' ? '🔁' : e.childBoxType === 'group' ? '🗂️' : '📦'} ${e.childBoxName ?? `박스 #${e.childBoxId}`}`;
                if (editItemId === e.id) {
                  return (
                    <tr key={e.id} className="border-b bg-blue-50">
                      {boxType === 'sequence' && <td className="py-1 text-xs text-gray-400">{idx + 1}</td>}
                      <td className="py-1">{label}</td>
                      <td colSpan={2}>
                        <div className="flex flex-wrap items-center gap-1 py-1">
                          <input type="datetime-local" value={editStart} onChange={(ev) => setEditStart(ev.target.value)}
                            className="border border-gray-300 px-1 py-0.5 text-xs" />
                          <span className="text-gray-400">~</span>
                          <input type="datetime-local" value={editEnd} onChange={(ev) => setEditEnd(ev.target.value)}
                            placeholder="무한" className="border border-gray-300 px-1 py-0.5 text-xs" />
                          <span className="text-[10px] text-gray-400">(종료 비우면 무한)</span>
                        </div>
                        <EventRef
                          fileHash={e.fileHash}
                          onApply={(startMs, endMs) => {
                            setEditStart(toLocalInput(startMs));
                            setEditEnd(endMs === null ? '' : toLocalInput(endMs));
                          }}
                        />
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <button disabled={busy} onClick={handleUpdateItem}
                          className="text-blue-600 hover:text-blue-800 disabled:opacity-50 mr-2">저장</button>
                        <button disabled={busy} onClick={() => setEditItemId(null)}
                          className="text-gray-500 hover:text-gray-700">취소</button>
                      </td>
                    </tr>
                  );
                }
                const inner = e.childBoxId !== null ? (childEntries[e.childBoxId] ?? []) : [];
                return (
                  <React.Fragment key={e.id}>
                  <tr className={`border-b ${ended ? 'opacity-40' : ''}`}
                    title={ended ? '종료된 항목 (재생 기간 지남 또는 영상 만료)' : undefined}>
                    {boxType === 'sequence' && <td className="py-1 text-xs text-gray-400">{idx + 1}</td>}
                    <td className="py-1 truncate max-w-[14rem]">{label}</td>
                    <td>{fmt(startMs)}</td>
                    <td>{endMs === null ? '무한' : fmt(endMs)}</td>
                    <td className="text-right whitespace-nowrap">
                      <button disabled={busy} onClick={() => beginEditItem({ id: e.id, startAt: startMs, endAt: endMs })}
                        className="text-blue-500 hover:text-blue-700 disabled:opacity-50 mr-2">수정</button>
                      <button disabled={busy} onClick={() => handleRemoveItem(e.id)}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50">삭제</button>
                    </td>
                  </tr>
                  {/* 자식 박스의 내용 — 항상 펼침. 편집은 그 박스에서(여기선 보기만). */}
                  {inner.map((ce) => (
                    <tr key={`c-${e.id}-${ce.id}`} className={`text-xs text-gray-500 ${ended ? 'opacity-40' : ''}`}>
                      {boxType === 'sequence' && <td />}
                      <td className="py-0.5 pl-5 truncate max-w-[14rem]" colSpan={3}>
                        <span className="text-gray-300 mr-1">└</span>
                        {ce.fileHash
                          ? `📹 ${titleOf(myVideos, ce.fileHash)}`
                          : `${ce.childBoxType === 'sequence' ? '🔁' : ce.childBoxType === 'group' ? '🗂️' : '📦'} ${ce.childBoxName ?? `박스 #${ce.childBoxId}`}`}
                      </td>
                      <td className="text-right text-[10px] text-gray-400 whitespace-nowrap">{e.childBoxType === 'group' ? '함께 재생' : e.childBoxType === 'sequence' ? '순환' : '일정 선택'}</td>
                    </tr>
                  ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* 드롭존 — 아래 영상 목록에서 드래그해 넣으면 기간을 묻는다 */}
        <div
          className={`border-2 border-dashed p-3 mb-2 transition-colors ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
          onDragOver={(e) => {
            const t = e.dataTransfer.types;
            if (t.includes('application/x-video-drag') || t.includes('application/x-videobox-drag')) {
              e.preventDefault(); setDragOver(true);
            }
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const v = e.dataTransfer.getData('application/x-video-drag');
            if (v) { onVideoDrop(v); return; }
            const b = e.dataTransfer.getData('application/x-videobox-drag');
            if (b) onBoxDrop(b);
          }}
        >
          {pendingDrop ? (
            <div className="bg-white border border-blue-300 p-3">
              <div className="text-sm font-medium mb-2">
                {pendingDrop.kind === 'video' ? '📹' : '📦'} {pendingDrop.title} — {boxType === 'sequence' ? '사용 기간' : '재생 기간'}을 정하세요
              </div>
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <label className="block text-xs text-gray-500">시작</label>
                  <input type="datetime-local" value={dropStart} onChange={(e) => setDropStart(e.target.value)} className="border border-gray-300 px-2 py-1" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">종료 <span className="text-gray-400">(비우면 무한)</span></label>
                  <input type="datetime-local" value={dropEnd} onChange={(e) => setDropEnd(e.target.value)} className="border border-gray-300 px-2 py-1" />
                </div>
                <button disabled={busy} onClick={handleAddPending}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 disabled:opacity-50">항목 추가</button>
                <button disabled={busy} onClick={() => setPendingDrop(null)}
                  className="text-gray-500 hover:text-gray-700 px-2 py-1">취소</button>
              </div>
              <EventRef
                fileHash={pendingDrop.kind === 'video' ? pendingDrop.fileHash : null}
                onApply={(startMs, endMs) => {
                  setDropStart(toLocalInput(startMs));
                  setDropEnd(endMs === null ? '' : toLocalInput(endMs));
                }}
              />
            </div>
          ) : (
            <div className="text-xs text-gray-500 text-center py-2">
              아래에서 <b>영상</b>이나 <b>박스</b>를 클릭(또는 여기로 드래그)하면 시작·종료 시각을 정해 항목으로 추가합니다.
            </div>
          )}
        </div>

        {/* 넣을 수 있는 것 1 — 영상 */}
        <label className="block text-sm font-medium mb-1">내 영상 목록 <span className="text-xs text-gray-400">(클릭하거나 위 드롭존으로 드래그해서 추가)</span></label>
        {myVideos.length === 0 ? (
          <div className="text-sm text-gray-500">영상이 없습니다. 먼저 영상을 업로드하세요.</div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {myVideos.map((v) => <BoxVideoThumb key={v.file_hash} video={v} onPick={() => pickVideo(v.file_hash, v.title)} />)}
          </div>
        )}

        {/* 넣을 수 있는 것 2 — 다른 박스(중첩). 영상과 완전히 같은 방식. */}
        <label className="block text-sm font-medium mb-1 mt-4">다른 영상 박스 <span className="text-xs text-gray-400">(박스 안에 박스 — 클릭해서 추가)</span></label>
        {siblingBoxes.length === 0 ? (
          <div className="text-sm text-gray-500">넣을 수 있는 다른 박스가 없습니다.</div>
        ) : (
          <div className="flex flex-wrap gap-1 md:gap-3">
            {siblingBoxes.map((b) => (
              // 영상 썸네일(BoxVideoThumb)과 동일하게 draggable — 클릭·드래그 둘 다 가능.
              <div key={b.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/x-videobox-drag', JSON.stringify({ boxId: b.id, name: b.name }));
                  e.dataTransfer.effectAllowed = 'copy';
                  (e.currentTarget as HTMLDivElement).style.opacity = '0.3';
                }}
                onDragEnd={(e) => { (e.currentTarget as HTMLDivElement).style.opacity = '1'; }}
                onClick={() => { if (!busy) pickChildBox(b.id, b.name); }}
                title="클릭 또는 위 드롭존으로 드래그: 이 박스에 넣기"
                className={`cursor-pointer border-2 border-dashed border-purple-400 bg-purple-50 hover:border-purple-600 px-3 py-2 w-[110px] md:w-28 ${busy ? 'opacity-40 pointer-events-none' : ''}`}>
                <div className="text-xs font-semibold text-purple-700 truncate">{b.boxType === 'sequence' ? '🔁' : b.boxType === 'group' ? '🗂️' : '📦'} {b.name}</div>
                <div className="text-[10px] text-gray-500 mt-1">{b.boxType === 'sequence' ? '시퀀싱' : b.boxType === 'group' ? '그룹' : '스케줄'} 박스</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-between mt-5">
          <button disabled={busy} onClick={handleDelete}
            className="text-red-600 hover:text-red-800 disabled:opacity-50">박스 삭제</button>
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 px-4 py-1">닫기</button>
        </div>
      </div>
    </div>
  );
};
